import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// CONTENT SAFETY MANAGER
// ============================================================

export const ContentSafetyManager = () => {
  const theme = useThemeClasses();
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [newWord, setNewWord] = useState('');
  const [newPhrase, setNewPhrase] = useState('');

  useEffect(() => {
    loadConfig();
    loadLogs();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-products/content-safety/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-products/content-safety/logs?limit=50`);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-products/content-safety/config`, config);
      toast.success('Content safety config updated successfully');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      const response = await axios.post(`${API}/admin/ai-products/content-safety/moderate`, {
        text: testText
      });
      setTestResult(response.data);
      toast.success('Test completed');
      loadLogs();
    } catch (error) {
      console.error('Test failed:', error);
      toast.error('Test failed');
    }
  };

  const addBannedWord = () => {
    if (newWord.trim() && !config.banned_words.includes(newWord.trim())) {
      setConfig({
        ...config,
        banned_words: [...config.banned_words, newWord.trim()]
      });
      setNewWord('');
    }
  };

  const removeBannedWord = (word) => {
    setConfig({
      ...config,
      banned_words: config.banned_words.filter(w => w !== word)
    });
  };

  const addBannedPhrase = () => {
    if (newPhrase.trim() && !config.banned_phrases.includes(newPhrase.trim())) {
      setConfig({
        ...config,
        banned_phrases: [...config.banned_phrases, newPhrase.trim()]
      });
      setNewPhrase('');
    }
  };

  const removeBannedPhrase = (phrase) => {
    setConfig({
      ...config,
      banned_phrases: config.banned_phrases.filter(p => p !== phrase)
    });
  };

  if (loading || !config) {
    return <div className="flex justify-center items-center p-8">Loading content safety settings...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Configuration */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Content Safety Configuration</h3>

        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm font-medium">Enable Content Safety</label>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={config.use_openai_moderation}
              onChange={(e) => setConfig({ ...config, use_openai_moderation: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm font-medium">Use OpenAI Moderation API</label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">OpenAI Moderation Threshold</label>
            <select
              value={config.openai_moderation_threshold}
              onChange={(e) => setConfig({ ...config, openai_moderation_threshold: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            >
              <option value="low">Low (More Permissive)</option>
              <option value="medium">Medium (Balanced)</option>
              <option value="high">High (Strict)</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={config.auto_reject_on_flag}
              onChange={(e) => setConfig({ ...config, auto_reject_on_flag: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm">Auto-reject flagged content</label>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={config.manual_review_required}
              onChange={(e) => setConfig({ ...config, manual_review_required: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm">Require manual review for flagged content</label>
          </div>

          <button
            onClick={handleSaveConfig}
            disabled={saving}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* Banned Words */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Banned Words</h3>
        
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addBannedWord()}
            placeholder="Add banned word..."
            className={`flex-1 px-4 py-2 border rounded-lg ${theme.input}`}
          />
          <button
            onClick={addBannedWord}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Add
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {config.banned_words.map((word, index) => (
            <span key={index} className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm flex items-center space-x-2">
              <span>{word}</span>
              <button
                onClick={() => removeBannedWord(word)}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </span>
          ))}
          {config.banned_words.length === 0 && (
            <p className="text-sm text-gray-500">No banned words configured</p>
          )}
        </div>
      </div>

      {/* Banned Phrases */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Banned Phrases</h3>
        
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addBannedPhrase()}
            placeholder="Add banned phrase..."
            className={`flex-1 px-4 py-2 border rounded-lg ${theme.input}`}
          />
          <button
            onClick={addBannedPhrase}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Add
          </button>
        </div>

        <div className="space-y-2">
          {config.banned_phrases.map((phrase, index) => (
            <div key={index} className="flex justify-between items-center px-4 py-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <span className="text-sm text-red-700 dark:text-red-300">{phrase}</span>
              <button
                onClick={() => removeBannedPhrase(phrase)}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          ))}
          {config.banned_phrases.length === 0 && (
            <p className="text-sm text-gray-500">No banned phrases configured</p>
          )}
        </div>
      </div>

      {/* Test Console */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Test Content Moderation</h3>
        
        <textarea
          value={testText}
          onChange={(e) => setTestText(e.target.value)}
          rows="4"
          placeholder="Enter text to test..."
          className={`w-full px-4 py-2 border rounded-lg ${theme.input} mb-4`}
        />

        <button
          onClick={handleTest}
          disabled={!testText.trim()}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          Test Content
        </button>

        {testResult && (
          <div className={`mt-4 p-4 rounded-lg ${
            testResult.flagged 
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          }`}>
            <h4 className="font-semibold mb-2">
              {testResult.flagged ? '❌ Content Flagged' : '✅ Content Approved'}
            </h4>
            <p className="text-sm">Action: <strong>{testResult.action_taken}</strong></p>
            {testResult.custom_flags.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-semibold">Custom Flags:</p>
                <ul className="text-sm list-disc list-inside">
                  {testResult.custom_flags.map((flag, i) => (
                    <li key={i}>{flag}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Logs */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Recent Moderation Logs</h3>
        
        <div className="space-y-2">
          {logs.slice(0, 10).map((log, index) => (
            <div key={index} className={`p-3 rounded-lg ${theme.bgSecondary}`}>
              <div className="flex justify-between items-start mb-2">
                <span className={`text-xs px-2 py-1 rounded ${
                  log.flagged 
                    ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                    : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                }`}>
                  {log.flagged ? 'Flagged' : 'Approved'}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
              <p className="text-sm truncate">{log.content}</p>
              <p className="text-xs text-gray-500 mt-1">Action: {log.action_taken}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// DESIGNER REQUESTS MANAGER
// ============================================================

export const DesignerRequestsManager = () => {
  const theme = useThemeClasses();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [editingRequest, setEditingRequest] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    loadRequests();
    loadAvailableOrders();
  }, [filterStatus]);

  const loadRequests = async () => {
    try {
      const url = filterStatus 
        ? `${API}/admin/ai-products/designer-requests?status=${filterStatus}`
        : `${API}/admin/ai-products/designer-requests`;
      const response = await axios.get(url);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests:', error);
      toast.error('Failed to load designer requests');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${API}/admin/orders`);
      // Get all orders and extract order numbers
      const orders = response.data || [];
      setAvailableOrders(orders.map(order => ({
        id: order.id,
        order_number: order.order_number || `#${order.id?.slice(-8)}`,
        customer_name: order.customer_name || order.user_email || 'Unknown',
        total: order.total || 0,
        created_at: order.created_at
      })));
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load available orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  const updateStatus = async (requestId, statusData) => {
    try {
      await axios.put(`${API}/admin/ai-products/designer-requests/${requestId}/status`, statusData);
      toast.success('Status updated successfully');
      loadRequests();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const addNote = async (requestId) => {
    if (!noteText.trim()) return;
    
    try {
      await axios.post(`${API}/admin/ai-products/designer-requests/${requestId}/note`, {
        author: 'admin',
        text: noteText
      });
      toast.success('Note added successfully');
      setNoteText('');
      loadRequests();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };
  
  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this designer request? This action cannot be undone.')) {
      return;
    }
    
    try {
      console.log('Deleting designer request:', requestId);
      await axios.delete(`${API}/admin/ai-products/designer-requests/${requestId}`);
      toast.success('Designer request deleted successfully');
      loadRequests();
    } catch (error) {
      console.error('Delete request error:', error.response?.data || error.message);
      toast.error(`Failed to delete request: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  const startEdit = (request) => {
    setEditingRequest(request.id);
    setEditForm({
      size: request.size || request.size_profile || '75mm',
      theme: request.theme || 'christmas',
      optional_text: request.optional_text || '',
      status: request.status_tracking?.status || 'pending',
      order_number: request.order_number || ''
    });
  };
  
  const cancelEdit = () => {
    setEditingRequest(null);
    setEditForm({});
  };
  
  const saveEdit = async (requestId) => {
    try {
      console.log('Updating designer request:', requestId, editForm);
      await axios.put(`${API}/admin/ai-products/designer-requests/${requestId}`, editForm);
      toast.success('Designer request updated successfully');
      setEditingRequest(null);
      setEditForm({});
      loadRequests();
    } catch (error) {
      console.error('Update request error:', error.response?.data || error.message);
      toast.error(`Failed to update request: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  const assignOrderNumber = async (requestId, orderNumber) => {
    try {
      await axios.put(`${API}/admin/ai-products/designer-requests/${requestId}/order-number`, {
        order_number: orderNumber
      });
      toast.success('Order number assigned successfully');
      loadRequests();
    } catch (error) {
      toast.error('Failed to assign order number');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading designer requests...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Designer Requests</h3>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={`px-4 py-2 border rounded-lg ${theme.input}`}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="grid gap-4">
        {requests.map((request) => (
          <div key={request.id} className={`${theme.card} rounded-lg shadow p-6`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-lg font-semibold">{request.customer_name}</h4>
                  {request.order_number && (
                    <span className="text-sm px-3 py-1 rounded-lg bg-blue-600 text-white font-bold shadow-sm">
                      Order #{request.order_number}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{request.customer_email}</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    request.status_tracking?.status === 'completed'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : request.status_tracking?.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                    {request.status_tracking?.status || 'pending'}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                    {request.size_profile}
                  </span>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                {editingRequest === request.id ? (
                  <>
                    <button
                      onClick={() => saveEdit(request.id)}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => cancelEdit()}
                      className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      ✖ Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(request)}
                      className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => deleteRequest(request.id)}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      🗑️ Delete
                    </button>
                    <select
                      value={request.status_tracking?.status || 'pending'}
                      onChange={(e) => updateStatus(request.id, { status: e.target.value })}
                      className={`px-3 py-1 text-sm border rounded ${theme.input}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button
                      onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      {selectedRequest?.id === request.id ? 'Hide' : 'Details'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Edit Form */}
            {editingRequest === request.id && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 rounded-lg space-y-4">
                <h5 className="font-semibold text-sm mb-3 text-yellow-900 dark:text-yellow-100">✏️ Edit Designer Request</h5>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Size Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Size</label>
                    <select
                      value={editForm.size}
                      onChange={(e) => setEditForm({...editForm, size: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${theme.input}`}
                    >
                      <option value="50mm">50mm (1 image)</option>
                      <option value="75mm">75mm (Up to 2 images)</option>
                      <option value="100mm">100mm (Up to 3 images)</option>
                    </select>
                  </div>

                  {/* Theme Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Background Theme</label>
                    <select
                      value={editForm.theme}
                      onChange={(e) => setEditForm({...editForm, theme: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${theme.input}`}
                    >
                      <optgroup label="Holidays">
                        <option value="christmas">🎄 Christmas</option>
                        <option value="halloween">🎃 Halloween</option>
                        <option value="easter">🐰 Easter</option>
                        <option value="valentines">💝 Valentine's Day</option>
                        <option value="thanksgiving">🦃 Thanksgiving</option>
                        <option value="newyear">🎊 New Year</option>
                      </optgroup>
                      <optgroup label="Seasons">
                        <option value="spring">🌸 Spring</option>
                        <option value="summer">☀️ Summer</option>
                        <option value="fall">🍂 Fall</option>
                        <option value="winter">❄️ Winter</option>
                      </optgroup>
                      <optgroup label="Special Occasions">
                        <option value="birthday">🎂 Birthday</option>
                        <option value="anniversary">💍 Anniversary</option>
                        <option value="wedding">💒 Wedding</option>
                        <option value="graduation">🎓 Graduation</option>
                        <option value="babyshower">👶 Baby Shower</option>
                      </optgroup>
                      <optgroup label="Memorial">
                        <option value="remembrance">🕊️ Remembrance</option>
                        <option value="inmemory">🌹 In Memory</option>
                        <option value="tribute">🙏 Tribute</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="nature">🌿 Nature</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Optional Text */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Custom Text (Optional)</label>
                    <input
                      type="text"
                      value={editForm.optional_text}
                      onChange={(e) => setEditForm({...editForm, optional_text: e.target.value})}
                      placeholder="E.g., Merry Christmas 2025"
                      maxLength={20}
                      className={`w-full px-3 py-2 border rounded ${theme.input}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">{editForm.optional_text?.length || 0}/20 characters</p>
                  </div>

                  {/* Order Number Linking */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Link to Order Number</label>
                    <select
                      value={editForm.order_number}
                      onChange={(e) => setEditForm({...editForm, order_number: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${theme.input}`}
                      disabled={loadingOrders}
                    >
                      <option value="">-- No Order Linked --</option>
                      {availableOrders.map(order => (
                        <option key={order.id} value={order.order_number}>
                          {order.order_number} - {order.customer_name} (${order.total})
                        </option>
                      ))}
                    </select>
                    {loadingOrders && <p className="text-xs text-gray-500 mt-1">Loading orders...</p>}
                  </div>
                </div>
              </div>
            )}

            {selectedRequest?.id === request.id && (
              <div className="mt-4 pt-4 border-t space-y-4">
                {/* Order Information */}
                <div className={`p-4 rounded-lg ${request.order_number ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-300' : 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300'}`}>
                  <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    {request.order_number ? '✅ Linked to Order' : '⚠️ Not Linked to Order'}
                  </h5>
                  {request.order_number ? (
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-green-700 dark:text-green-300">
                        Order #{request.order_number}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        This designer request is linked to an order in the system. You can find this order in Orders Management.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      This designer request is not yet linked to any order. Use the Edit button to link it to an existing order number.
                    </p>
                  )}
                </div>
                
                <div>
                  <h5 className="font-semibold text-sm mb-2">Background Theme:</h5>
                  <p className="text-sm capitalize">{request.theme || 'Not specified'}</p>
                </div>
                <div>
                  <h5 className="font-semibold text-sm mb-2">Custom Text:</h5>
                  <p className="text-sm">{request.optional_text || 'None'}</p>
                </div>
                <div>
                  <h5 className="font-semibold text-sm mb-2">Uploaded Images ({request.image_count || 0}):</h5>
                  {request.images && request.images.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {request.images.map((img, idx) => {
                        // Convert file system path to API path
                        const getApiPath = (path) => {
                          if (!path) return '';
                          // Remove leading /ai-products/ and prepend with /api/ai-products/files/
                          const cleanPath = path.startsWith('/ai-products/') 
                            ? path.substring('/ai-products/'.length) 
                            : path.startsWith('ai-products/') 
                            ? path.substring('ai-products/'.length)
                            : path;
                          return `${API}/ai-products/files/${cleanPath}`;
                        };
                        
                        const thumbnailUrl = getApiPath(img.thumbnail_path || img.path);
                        const fullImageUrl = getApiPath(img.path);
                        
                        return (
                          <div key={idx} className="space-y-2">
                            <div className="relative group">
                              <img 
                                src={thumbnailUrl}
                                alt={img.original_filename}
                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-blue-500 transition-all"
                                onClick={() => window.open(fullImageUrl, '_blank')}
                                onError={(e) => {
                                  console.error('Image load error:', thumbnailUrl);
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                                <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-bold">View Full</span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 truncate" title={img.original_filename}>
                              {img.original_filename}
                            </p>
                            <a
                              href={fullImageUrl}
                              download={img.original_filename}
                              className="block w-full text-center px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                            >
                              📥 Download
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No images uploaded</p>
                  )}
                </div>
                <div>
                  <h5 className="font-semibold text-sm mb-2">Design Details:</h5>
                  <div className="text-sm" dangerouslySetInnerHTML={{ __html: request.design_details }} />
                </div>
                <div>
                  <h5 className="font-semibold text-sm mb-2">Add Note:</h5>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    rows="2"
                    className={`w-full px-4 py-2 border rounded-lg ${theme.input} mb-2`}
                  />
                  <button
                    onClick={() => addNote(request.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Add Note
                  </button>
                </div>
                {request.status_tracking?.notes && request.status_tracking.notes.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-sm mb-2">Notes:</h5>
                    <div className="space-y-2">
                      {request.status_tracking.notes.map((note, i) => (
                        <div key={i} className={`p-2 rounded ${theme.bgSecondary}`}>
                          <p className="text-xs text-gray-500">{note.author} - {new Date(note.timestamp).toLocaleString()}</p>
                          <p className="text-sm">{note.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// JOB QUEUE MONITOR
// ============================================================

export const JobQueueMonitor = () => {
  const theme = useThemeClasses();
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    loadJobs();
    loadHealth();
  }, [filterStatus]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadJobs();
        loadHealth();
      }, 5000); // Refresh every 5 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, filterStatus]);

  const loadJobs = async () => {
    try {
      const url = filterStatus 
        ? `${API}/admin/ai-products/jobs/queue?status=${filterStatus}`
        : `${API}/admin/ai-products/jobs/queue`;
      const response = await axios.get(url);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-products/system/health`);
      setHealth(response.data);
    } catch (error) {
      console.error('Failed to load health:', error);
    }
  };

  const retryJob = async (jobId) => {
    try {
      await axios.post(`${API}/admin/ai-products/jobs/${jobId}/retry`);
      toast.success('Job queued for retry');
      loadJobs();
    } catch (error) {
      console.error('Failed to retry job:', error);
      toast.error('Failed to retry job');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading job queue...</div>;
  }

  return (
    <div className="space-y-6">
      {/* System Health */}
      {health && (
        <div className="grid grid-cols-4 gap-4">
          <div className={`${theme.card} rounded-lg shadow p-4`}>
            <p className="text-sm text-gray-600 dark:text-gray-400">Active Jobs</p>
            <p className="text-3xl font-bold text-blue-600">{health.active_jobs}</p>
          </div>
          <div className={`${theme.card} rounded-lg shadow p-4`}>
            <p className="text-sm text-gray-600 dark:text-gray-400">Queued Jobs</p>
            <p className="text-3xl font-bold text-yellow-600">{health.queued_jobs}</p>
          </div>
          <div className={`${theme.card} rounded-lg shadow p-4`}>
            <p className="text-sm text-gray-600 dark:text-gray-400">Success Rate (24h)</p>
            <p className="text-3xl font-bold text-green-600">{health.success_rate_24h}%</p>
          </div>
          <div className={`${theme.card} rounded-lg shadow p-4`}>
            <p className="text-sm text-gray-600 dark:text-gray-400">Avg Processing Time</p>
            <p className="text-3xl font-bold text-purple-600">{health.avg_processing_time.toFixed(1)}s</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold">Job Queue</h3>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`px-4 py-2 border rounded-lg ${theme.input}`}
          >
            <option value="">All Status</option>
            <option value="queued">Queued</option>
            <option value="processing">Processing</option>
            <option value="ready">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="text-purple-600"
          />
          <span className="text-sm">Auto-refresh (5s)</span>
        </label>
      </div>

      {/* Jobs Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={theme.bgSecondary}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Job ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Duration</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job, index) => (
                <tr key={job.job_id} className={`border-t ${theme.border}`}>
                  <td className="px-4 py-3 text-sm font-mono">{job.job_id.substring(0, 8)}...</td>
                  <td className="px-4 py-3 text-sm">{job.job_type || 'lithophane_preview'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs rounded ${
                      job.status === 'ready'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : job.status === 'failed'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                        : job.status === 'processing'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                    }`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(job.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {job.completed_at 
                      ? `${Math.round((new Date(job.completed_at) - new Date(job.created_at)) / 1000)}s`
                      : '-'
                    }
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {job.status === 'failed' && (
                      <button
                        onClick={() => retryJob(job.job_id)}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
